import { IkeToken } from '@ikegps/core-api';
import { FormModelField, FormModelFieldSubForm } from '@ikegps/core-shared';
import { action, observable, makeObservable } from 'mobx';
import { Icon } from '../../../components/icon';
import { MaterialIcon } from '../../../components/icon/icons';
import { MenuComponent } from '../../../components/menu/menu';
import { type MenuItem, MenuItemComponent } from '../../../components/menu/menu.item';
import { CSS as MenuItemCSS } from '../../../components/menu/menu.item.style';
import { addStyles, DefaultTheme } from '../../../components/theme';
import { T, Translate } from '../../../i18n';
import { m } from '../../../mithril';
import { Services } from '../../../services';
import { FormDesigner } from '../form.designer';
import { FIELD_TYPE_DISPLAY } from '../form.palette';
import { FormFieldEditor } from './form.field.editor';

const CSS = {
	container: 'c-FdcHeight',
	title: 'c-FdcHeight__title',
	list: 'c-FdcHeight__list',
	menu: 'c-FdcHeight__menu',
	menuContainer: 'c-FdcHeight__menuContainer',
	option: 'c-FdcHeight__option',
	value: 'c-FdcHeight__value',
	ghost: 'c-FdcHeight__ghost',
	icon: {
		icon: 'c-FdcHeight__icon',
		label: 'c-FdcHeight__icon-label',
	},
	selected: 'c-FdcHeight__selected',
};

addStyles({
	[CSS.container]: {
		width: '100%',
		display: 'flex',
		'flex-direction': 'column',
		'& .mdl-button': {
			height: 'fit-content',
			width: 'fit-content',
		},
	},
	[CSS.list]: {
		display: 'flex',
		width: '100%',
		'align-items': 'center',
	},
	[CSS.title]: {
		'font-weight': 'bold',
		'margin-bottom': DefaultTheme.space(1),
		color: DefaultTheme.font.color.supporting,
	},
	[CSS.option]: {
		width: '320px',
		'text-align': 'left',
		display: 'flex',
		'align-items': 'center',
	},
	[CSS.selected]: {
		width: '320px',
		'text-align': 'left',
		display: 'flex',
		'align-items': 'center',
	},
	[CSS.ghost]: {
		color: DefaultTheme.font.color.supporting,
	},
	[CSS.value]: {
		width: '520px', // TODO this is from the input width
		'text-align': 'left',
	},
	[CSS.icon.icon]: {
		margin: '0 4px',
		'font-style': 'normal',
	},
	[CSS.icon.label]: {
		'border-radius': '50%',
		border: '1px solid ' + DefaultTheme.background.grey,
		'background-color': DefaultTheme.background.lightGrey,
		'font-weight': 'bold',
		'font-size': '12px',
		height: '16px',
		width: '16px',
		display: 'flex',
		'align-items': 'center',
		'justify-content': 'center',
		margin: '0 8px',
	},
	[CSS.menu]: {
		[`& .${MenuItemCSS.item}`]: {
			'padding-top': DefaultTheme.space(1),
			'padding-bottom': DefaultTheme.space(1),
			'line-height': DefaultTheme.space(6),
		},
	},
	[CSS.menuContainer]: {
		display: 'flex',
		'flex-direction': 'column',
		position: 'relative',
	},
});

interface HeightFieldSelectOptions {
	fieldId: string;
	displayName: string;
	fieldType?: string;
	parentDisplayName?: string;
}

export class FormFieldEditorHeight extends FormFieldEditor<FormModelField> {
	annotationMenu: MenuComponent;
	@observable annotationLabel: HeightFieldSelectOptions | null = null;
	list: HeightFieldSelectOptions[];

	constructor(designer: FormDesigner, field) {
		super(designer, field);

		makeObservable(this);

		this.list = this.getListOfTitleFields(this.field.parent);
		this.list.unshift({ displayName: 'Default', fieldId: null, fieldType: null });

		this.annotationMenu = new MenuComponent({
			items: () => this.viewListItems(),
			className: CSS.container,
		});

		this.annotationLabel = this.list.find((l) => l.fieldId === this.field.annotationLabelFieldId) || this.list[0];
	}

	protected getListOfTitleFields(subform: FormModelFieldSubForm) {
		let list: HeightFieldSelectOptions[] = [];
		if (subform.parent != null) {
			list.push(...this.getListOfTitleFields(subform.parent));
		}
		list.push(
			...subform.fields.map((x) => {
				let item: HeightFieldSelectOptions = {
					displayName: x.displayName,
					fieldId: x.fieldId.split(':').pop(),
					fieldType: x.type,
					parentDisplayName: x.parent.parent ? x.parent.displayName : undefined,
				};
				if (x.fieldId === this.field.fieldId) {
					item.displayName = 'Self-Title (' + x.displayName + ')';
				}
				return item;
			})
		);
		return list;
	}

	protected getIcon(icon: string | null) {
		return icon
			? Icon.view(FIELD_TYPE_DISPLAY[icon].icon, { className: CSS.icon.icon })
			: m(
					'i',
					{
						className: CSS.icon.label + ' ' + CSS.icon.icon,
						title: Translate(T.FORM_OPTION_LABEL),
					},
					'L'
				);
	}

	protected getLabelWithNode() {
		if (Services.Token.has(IkeToken.FEATURE_MARKER_LABEL)) {
			return m('div', { className: CSS.container }, [
				m('div', { className: `${CSS.option} ${CSS.title}` }, 'Label With'),
				m('div', { className: CSS.menuContainer }, [
					m(
						'button',
						{
							className: `mdl-button ${CSS.selected}`,
							title: 'LABEL WITH',
							onclick: this.annotationMenu.show,
						},
						[
							this.getIcon(this.annotationLabel.fieldType),
							this.annotationLabel.parentDisplayName
								? `${this.annotationLabel.parentDisplayName} > ${this.annotationLabel.displayName}`
								: this.annotationLabel.displayName,
							Icon.view(MaterialIcon.KEY_DOWN),
						]
					),
					this.annotationMenu.view(),
				]),
			]);
		}

		return null;
	}

	//default view
	override viewInfoRW() {
		const node = m('div', { className: CSS.list }, [this.getLabelWithNode()]);

		return node;
	}

	viewListItems(): MenuItem[] {
		const items: MenuItem[] = [];
		for (let i = 0; i < this.list.length; i++) {
			const option = this.list[i];
			items.push({
				view: MenuItemComponent.view([
					this.getIcon(option.fieldType),
					option.parentDisplayName ? `${option.parentDisplayName} > ${option.displayName}` : option.displayName,
				]),
				action: () => this.changeAnnotationField(option),
			});
		}

		return items;
	}

	@action.bound protected changeAnnotationField(key: HeightFieldSelectOptions): any {
		this.setAnnotationLabelFieldId(key.fieldId);
		this.annotationLabel = key;
	}
}
